import { useState, useMemo, useCallback } from "react";
import { useWindowSize } from "react-use";
import { usePathname } from "next/navigation";

import { GlobalType } from "@/types";

import { headerVariant, type HeaderVariantProps } from "./header.variants";

export type UseHeaderProps = HeaderVariantProps & {
  className?: string;
  style?: Record<string, string | number>;
  data: GlobalType;
};

export type HeaderState = {
  opened: boolean;
  sideMenuOpened: boolean;
  sideMenuCategory: string;
};

const MIN_WIDTH_FOR_DESKTOP_MENU = 1024;

export const useHeader = (props: UseHeaderProps) => {
  const { className = "", data } = props;
  const { siteName } = data;
  const { width } = useWindowSize();
  const pathname = usePathname();

  const [state, setState] = useState<HeaderState>({
    opened: false,
    sideMenuOpened: false,
    sideMenuCategory: "",
  });
  const { opened, sideMenuOpened, sideMenuCategory } = state;

  const styles = headerVariant({ opened });

  const dispatch = useCallback((payload: Partial<HeaderState>) => {
    setState((prevState: HeaderState) => ({ ...prevState, ...payload }));
  }, []);

  const isMobile = useMemo(() => width < MIN_WIDTH_FOR_DESKTOP_MENU, [width]);

  const onHandleMenu = useCallback(
    (active: HeaderState["opened"]) => {
      dispatch({ opened: active, sideMenuOpened: false });
    },
    [dispatch],
  );

  const onHandleOpenSideMenu = useCallback(
    (
      active: HeaderState["sideMenuOpened"],
      category: HeaderState["sideMenuCategory"],
    ) => {
      dispatch({ sideMenuOpened: active, sideMenuCategory: category });
    },
    [dispatch],
  );

  const menu = useMemo(() => {
    return data.menu.map((item) => {
      let isActive = false;
      if (item.link && item.link === "/" && pathname === "/") {
        isActive = true;
      } else if (item.link && item.link !== "/" && pathname.includes(item.link)) {
        isActive = true;
      }

      return {
        ...item,
        active: isActive,
      };
    });
  }, [data.menu, pathname]);

  const docsMenu = useMemo(() => {
    return data.docsMenu?.find((item) => item.slug === sideMenuCategory);
  }, [data.docsMenu, sideMenuCategory]);

  return {
    className,
    styles,
    onHandleMenu,
    opened,
    menu,
    siteName,
    docsMenu,
    sideMenuOpened,
    onHandleOpenSideMenu,
    isMobile,
  };
};
